/**
 * EmbeddedCanvasRenderer - Runtime component for embedded canvas elements.
 *
 * Fetches child canvas definition from API.
 * Filters child records by parent key value.
 * Auto-detects FK field using naming conventions.
 * Renders child records as form or list depending on count.
 * Supports independent form state per embedded canvas.
 * CRUD operations with parent key auto-linking.
 */

import { resolveElementType } from "../widgetElementType";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardContent,
  Stack,
  Divider,
  Chip,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { useCanvasData } from "../../../hooks/useCanvases";
import { useChildCanvasPreview } from "../../../hooks/useChildCanvasPreview";
import {
  isWritableAccess,
  visibleRuntimeElements,
} from "../../../views/runtimeElementAccess";
import {
  CanvasRenderer,
  type FieldState,
  type RenderableElement,
} from "../CanvasRenderer";
import {
  buildDataSourceSelectionKey,
  buildRuntimeFilterScopeKey,
  CanvasRuntimeProvider,
  useCanvasRuntime,
} from "../CanvasRuntimeContext";
import type {
  CanvasElement,
  Canvas,
  CanvasDataSource,
  CanvasGridConfig,
} from "../../../types";
import {
  getEmbeddingConfig,
  type EmbeddingConfig,
} from "./EmbeddedCanvasElement";
import { CanvasViewGridRenderer } from "./CanvasViewGridRenderer";
import { CanvasTimelineRenderer } from "./CanvasTimelineRenderer";
import { CanvasDataWidgetRenderer } from "./CanvasDataWidgetRenderer";
import { withEmbeddingPath } from "../../../services/viewService";
import { CanvasAutoHeightContext } from "../contentHeight";

const RUNTIME_GRID_CONFIG: CanvasGridConfig = {
  columns: 12,
  rowHeight: 60,
  gap: 8,
  minRows: 3,
};

export interface EmbeddedCanvasRendererProps {
  /** The canvas element definition */
  element: CanvasElement;
  /** Parent record data (for filtering child records) */
  parentData?: Record<string, unknown>;
  /** Workspace ID for API calls */
  workspaceId: string;
  /** Parent canvas ID */
  parentCanvasId?: string | number;
  /** Callback when child data changes */
  onChildDataChange?: (
    childCanvasId: string | number,
    data: Record<string, unknown>[],
  ) => void;
  /** Whether to allow creating new child records */
  allowCreate?: boolean;
  /** Whether to allow editing child records */
  allowEdit?: boolean;
  /** Whether to allow deleting child records */
  allowDelete?: boolean;
}

interface ChildRecord {
  __row_id: string | number;
  [key: string]: unknown;
}

/**
 * Gets the parent key value from parent data using the configured field path.
 */
function getParentKeyValue(
  parentData: Record<string, unknown> | undefined,
  config: EmbeddingConfig,
): unknown {
  if (!parentData || !config.parentKeyField) return undefined;

  // Handle nested paths like "data_source.field_name"
  const parts = config.parentKeyField.split(".");
  let value: unknown = parentData;

  for (const part of parts) {
    if (
      value &&
      typeof value === "object" &&
      part in (value as Record<string, unknown>)
    ) {
      value = (value as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return value;
}

/**
 * Auto-detects the FK field in child canvas using naming conventions.
 */
function autoDetectFkField(
  childCanvas: Canvas,
  parentKeyField: string | undefined,
): string | null {
  if (!parentKeyField) return null;

  const parentFieldLower = parentKeyField.toLowerCase();
  const commonPatterns = [
    parentKeyField, // Exact match
    `${parentFieldLower}_id`,
    `${parentFieldLower}id`,
    parentFieldLower.replace("_id", "") + "_id",
    "parent_id",
    "fk_" + parentFieldLower,
  ];

  // Look through child canvas data sources and their views
  for (const ds of childCanvas.data_sources || []) {
    // Check if any field in the view matches the patterns
    const dsConfig = ds.config as unknown as {
      fields?: Array<{ alias?: string; column_name?: string }>;
    };
    for (const field of dsConfig?.fields || []) {
      const fieldName = (field.alias || field.column_name || "").toLowerCase();
      if (commonPatterns.some((p) => fieldName === p.toLowerCase())) {
        return field.alias || field.column_name || null;
      }
    }
  }

  return null;
}

/**
 * Transforms backend canvas definition (from /canvases/:id/full) to RenderableElement[].
 */
export function transformToRenderableElements(
  canvas: Canvas | null,
): RenderableElement[] {
  // Backend shape (pydantic) uses: element_type, config_json, grid_column, column_span, etc.
  // Frontend shape (builder) uses: type, props, layout.
  const rawElements = visibleRuntimeElements(
    ((canvas as any)?.elements || []) as any[],
  );
  const rawEmbeddings = ((canvas as any)?.embeddings || []) as any[];
  const embeddingByElementId = new Map<string, any>();
  for (const embedding of rawEmbeddings) {
    const elementId = embedding?.element_id || embedding?.elementId;
    if (elementId) embeddingByElementId.set(String(elementId), embedding);
  }

  return rawElements.map((el, idx) => {
    const rawType = String(el.element_type || el.type || "").toLowerCase();
    const type = resolveElementType(rawType, el.config_json || el.props);
    const baseProps = el.config_json || el.props || {};
    const embedding = el.id
      ? embeddingByElementId.get(String(el.id))
      : undefined;
    const props =
      type === "embedded_canvas" && embedding
        ? {
            childCanvasId: embedding.child_canvas_id || embedding.childCanvasId,
            parentDataSourceAlias:
              embedding.parent_data_source_alias ||
              embedding.parentDataSourceAlias,
            parentKeyField:
              embedding.parent_key_field || embedding.parentKeyField,
            childDataSourceAlias:
              embedding.child_data_source_alias ||
              embedding.childDataSourceAlias,
            childFilterField:
              embedding.child_filter_field || embedding.childFilterField,
            ...(baseProps || {}),
          }
        : baseProps || {};
    const variant = (props.fieldType || props.variant) as string | undefined;

    const rawBinding = el.data_binding;
    const data_binding = rawBinding?.field_path
      ? {
          data_source_id:
            rawBinding.data_source_alias || rawBinding.data_source_id || "main",
          field_path: rawBinding.field_path,
        }
      : undefined;

    return {
      id: String(el.id || `el-${idx}`),
      type,
      effectiveAccess: el.effective_access,
      variant,
      props,
      gridColumn: el.grid_column ?? el.gridColumn ?? 1,
      gridRow: el.grid_row ?? el.gridRow ?? idx + 1,
      columnSpan: el.column_span ?? el.columnSpan ?? 12,
      rowSpan: el.row_span ?? el.rowSpan ?? 1,
      data_binding,
      parentElementId: el.parent_element_id ?? el.parentElementId ?? undefined,
      parentPanelId: el.parent_panel_id ?? el.parentPanelId ?? undefined,
      displayOrder: el.display_order ?? el.displayOrder ?? idx,
      isVisible: el.is_visible ?? el.isVisible,
      visibilityRules: el.visibility_rules ?? el.visibilityRules ?? undefined,
    } satisfies RenderableElement;
  });
}

function getCanvasDataSources(canvas: Canvas | null): CanvasDataSource[] {
  const rawDataSources = ((canvas as any)?.data_sources || []) as any[];
  return rawDataSources.map((source, idx) => ({
    id: String(source.alias || source.id || `ds-${idx}`),
    name: String(source.alias || source.name || source.id || `ds-${idx}`),
    type: (source.type || "view") as CanvasDataSource["type"],
    view_id: source.view_id,
    schema_id: source.schema_id,
    is_primary: source.is_primary,
    config: source.config_json || source.config || {},
  }));
}

function toCanvasElement(element: RenderableElement): CanvasElement {
  return {
    id: element.id,
    type: element.type as CanvasElement["type"],
    props: element.props,
    data_binding: element.data_binding as any,
    layout: {
      gridColumn: element.gridColumn,
      gridRow: element.gridRow,
      columnSpan: element.columnSpan,
      rowSpan: element.rowSpan,
    },
    parentElementId: element.parentElementId,
    parentPanelId: element.parentPanelId,
    displayOrder: element.displayOrder,
  };
}

/**
 * EmbeddedCanvasRenderer - Renders an embedded canvas in runtime mode.
 */
export function EmbeddedCanvasRenderer({
  element,
  parentData,
  workspaceId,
  parentCanvasId,
  onChildDataChange,
  allowCreate: embeddingAllowsCreate = true,
  allowEdit: embeddingAllowsEdit = true,
  allowDelete: embeddingAllowsDelete = true,
}: EmbeddedCanvasRendererProps) {
  const rawConfig = getEmbeddingConfig(element);
  const authFetch = useAuthFetch();
  const runtime = useCanvasRuntime();
  const autoHeight = useContext(CanvasAutoHeightContext);

  // Memoize config by its primitive values to avoid new object references every render
  const config = useMemo(
    () => rawConfig,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rawConfig.childCanvasId,
      rawConfig.childFilterField,
      rawConfig.parentKeyField,
      rawConfig.childDataSourceAlias,
      rawConfig.parentDataSourceAlias,
      rawConfig.presentation,
      rawConfig.showTitle,
    ],
  );

  const compact = config.presentation === "compact";
  const showTitle = config.showTitle !== false;
  const surfaceSx = compact
    ? {
        height: autoHeight ? "auto" : "100%",
        p: 0,
        border: 0,
        bgcolor: "transparent",
        boxShadow: "none",
      }
    : { height: autoHeight ? "auto" : "100%" };

  // Use refs for callback-only dependencies to break the re-render cycle
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;
  const onChildDataChangeRef = useRef(onChildDataChange);
  onChildDataChangeRef.current = onChildDataChange;

  // Fetch child canvas definition
  const {
    canvas: childCanvas,
    loading: canvasLoading,
    error: canvasError,
  } = useChildCanvasPreview(workspaceId, config.childCanvasId);

  // Child records state
  const [childRecords, setChildRecords] = useState<ChildRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [loadedRecordsScope, setLoadedRecordsScope] = useState<string | null>(
    null,
  );
  const [recordsError, setRecordsError] = useState<string | null>(null);

  // Form state for each child record
  const [formStates, setFormStates] = useState<
    Record<string, Record<string, FieldState>>
  >({});
  const [editingRecordId, setEditingRecordId] = useState<
    string | number | null
  >(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newRecordData, setNewRecordData] = useState<Record<string, unknown>>(
    {},
  );

  // Expansion state for list view
  const [expandedRecords, setExpandedRecords] = useState<Set<string | number>>(
    new Set(),
  );

  // Counter to trigger re-fetch after CRUD operations (avoids race conditions with useEffect cleanup)
  const [refreshCounter, setRefreshCounter] = useState(0);

  const parentCanvasScopeId =
    parentCanvasId !== undefined && parentCanvasId !== null
      ? String(parentCanvasId)
      : runtime?.canvasId || null;
  const embeddingPath = useMemo(
    () =>
      parentCanvasScopeId
        ? [
            ...(runtime?.embeddingPath || []),
            { canvasId: parentCanvasScopeId, elementId: String(element.id) },
          ]
        : undefined,
    [parentCanvasScopeId, element.id, runtime?.embeddingPath],
  );
  const parentDataSourceAlias = config.parentDataSourceAlias || "main";
  const parentSelection = runtime?.interactionBus.getSelectedRow({
    canvasId: parentCanvasScopeId,
    dataSourceAlias: parentDataSourceAlias,
  });
  const selectedParentData = useMemo(() => {
    if (!parentSelection) return null;
    const rowData = { ...parentSelection.rowData };
    const selectedRowId = parentSelection.rowId;
    if (rowData.id === undefined && selectedRowId !== undefined) {
      rowData.id = selectedRowId;
    }
    if (
      config.parentKeyField &&
      rowData[config.parentKeyField] === undefined &&
      selectedRowId !== undefined
    ) {
      rowData[config.parentKeyField] = selectedRowId;
    }
    return rowData;
  }, [config.parentKeyField, parentSelection]);
  const effectiveParentData = selectedParentData || parentData;
  const parentRowId =
    parentSelection?.rowId ?? runtime?.rowId ?? effectiveParentData?.__row_id;

  // Get parent key value
  const parentKeyValue = getParentKeyValue(effectiveParentData, config);

  // Auto-detect FK field if not configured
  const effectiveFkField = useMemo(() => {
    return (
      config.childFilterField ||
      (childCanvas
        ? autoDetectFkField(childCanvas, config.parentKeyField)
        : null)
    );
  }, [config.childFilterField, config.parentKeyField, childCanvas]);

  const childCanvasScopeId =
    childCanvas?.id !== undefined && childCanvas?.id !== null
      ? String(childCanvas.id)
      : config.childCanvasId !== undefined && config.childCanvasId !== null
        ? String(config.childCanvasId)
        : null;
  const childDataSourceAlias = config.childDataSourceAlias || "main";
  const childDataSources = useMemo(
    () => getCanvasDataSources(childCanvas),
    [childCanvas],
  );
  const childViewId = (
    childDataSources.find((source) => source.name === childDataSourceAlias) ||
    childDataSources[0]
  )?.view_id;
  // A refresh may retain mounted fields only within the exact loaded scope.
  const recordsScope = JSON.stringify([
    workspaceId,
    parentCanvasScopeId,
    element.id,
    config.childCanvasId,
    childCanvasScopeId,
    childViewId,
    childDataSourceAlias,
    parentDataSourceAlias,
    effectiveFkField,
    config.parentKeyField,
    parentKeyValue,
    parentRowId,
  ]);
  const recordsScopeMatches = loadedRecordsScope === recordsScope;
  const currentRecordsContext = useRef({
    scope: recordsScope,
    loading: recordsLoading,
  });
  currentRecordsContext.current = {
    scope: recordsScope,
    loading: recordsLoading,
  };
  const mayWriteCurrentRecords = () =>
    recordsScopeMatches &&
    currentRecordsContext.current.scope === recordsScope &&
    !currentRecordsContext.current.loading;

  useEffect(() => {
    setChildRecords([]);
    setFormStates({});
    setLoadedRecordsScope(null);
    setRecordsError(null);
    setEditingRecordId(null);
    setIsCreating(false);
    setNewRecordData({});
    setExpandedRecords(new Set());
  }, [recordsScope]);

  const {
    data: childCapabilities,
    loading: capabilitiesLoading,
    error: capabilitiesError,
  } = useCanvasData(
    workspaceId,
    childCanvasScopeId,
    undefined,
    childDataSourceAlias,
  );
  // Capabilities do not need a record selector (the /data selector is UUID-only).
  // Actual record IDs and authorization remain on the save/delete requests.
  // The API composes the child canvas/View grants. Each embedding level can
  // restrict those actions further, but must never grant a different action.
  const capabilitiesReady = !capabilitiesLoading && !capabilitiesError;
  const allowCreate =
    embeddingAllowsCreate &&
    capabilitiesReady &&
    childCapabilities?.can_create === true;
  const allowEdit =
    embeddingAllowsEdit &&
    capabilitiesReady &&
    childCapabilities?.can_edit === true;
  const allowDelete =
    embeddingAllowsDelete &&
    capabilitiesReady &&
    childCapabilities?.can_delete === true;

  const renderableElements = useMemo(
    () =>
      transformToRenderableElements(childCanvas).map((childElement) =>
        !allowCreate && !allowEdit
          ? { ...childElement, effectiveAccess: "read" as const }
          : childElement,
      ),
    [allowCreate, allowEdit, childCanvas],
  );
  const writableChildFields = useMemo(
    () =>
      renderableElements.filter(
        (childElement) =>
          childElement.type === "field" &&
          !!childElement.data_binding?.field_path &&
          isWritableAccess(childElement.effectiveAccess) &&
          !childElement.props.readonly &&
          !childElement.props.is_readonly,
      ),
    [renderableElements],
  );
  const canCreateChild =
    allowCreate &&
    recordsScopeMatches &&
    !recordsLoading &&
    writableChildFields.length > 0;
  const canEditChild =
    allowEdit &&
    recordsScopeMatches &&
    !recordsLoading &&
    writableChildFields.length > 0;
  const hasCollectionWidget = renderableElements.some(
    (childElement) =>
      childElement.type === "view_grid" ||
      childElement.type === "timeline" ||
      childElement.type === "kanban" ||
      childElement.type === "calendar" ||
      childElement.type === "hierarchy",
  );

  const renderNestedViewGrid = useCallback(
    (childElement: RenderableElement) => (
      <CanvasViewGridRenderer
        element={childElement}
        dataSources={childDataSources}
        canvasId={childCanvasScopeId}
      />
    ),
    [childCanvasScopeId, childDataSources],
  );

  const renderNestedTimeline = useCallback(
    (childElement: RenderableElement) => (
      <CanvasTimelineRenderer
        element={childElement}
        dataSources={childDataSources}
        canvasId={childCanvasScopeId}
      />
    ),
    [childCanvasScopeId, childDataSources],
  );

  const renderNestedDataWidget = useCallback(
    (childElement: RenderableElement) => (
      <CanvasDataWidgetRenderer
        element={childElement}
        dataSources={childDataSources}
        canvasId={childCanvasScopeId}
      />
    ),
    [childCanvasScopeId, childDataSources],
  );

  const renderNestedEmbeddedCanvas = useCallback(
    (
      childElement: RenderableElement,
      childParentData?: Record<string, unknown>,
    ) => (
      <EmbeddedCanvasRenderer
        element={toCanvasElement(childElement)}
        parentData={childParentData}
        workspaceId={workspaceId}
        parentCanvasId={childCanvasScopeId || undefined}
        allowCreate={
          allowCreate && isWritableAccess(childElement.effectiveAccess)
        }
        allowEdit={allowEdit && isWritableAccess(childElement.effectiveAccess)}
        allowDelete={
          allowDelete && isWritableAccess(childElement.effectiveAccess)
        }
      />
    ),
    [allowCreate, allowDelete, allowEdit, childCanvasScopeId, workspaceId],
  );

  const upsertRuntimeFilter = runtime?.interactionBus.upsertRuntimeFilter;
  const removeRuntimeFilter = runtime?.interactionBus.removeRuntimeFilter;
  useEffect(() => {
    if (
      !upsertRuntimeFilter ||
      !removeRuntimeFilter ||
      !childCanvasScopeId ||
      !effectiveFkField
    )
      return undefined;

    const filterId = `embedded:${element.id}:${childCanvasScopeId}:${childDataSourceAlias}`;
    if (parentKeyValue === undefined) {
      removeRuntimeFilter(filterId);
      return undefined;
    }

    const parentSelectionKey =
      parentSelection?.selectionKeys[0] ||
      buildDataSourceSelectionKey(parentCanvasScopeId, parentDataSourceAlias);
    const provenancePath = Array.from(
      new Set([...(parentSelection?.provenancePath || []), parentSelectionKey]),
    );
    const targetScopeKey = buildRuntimeFilterScopeKey(
      childCanvasScopeId,
      childDataSourceAlias,
    );

    upsertRuntimeFilter({
      id: filterId,
      sourceEventId: `embedded-filter:${element.id}:${String(parentKeyValue)}`,
      sourceElementId: parentSelection?.elementId || element.id,
      sourceDataSourceAlias: parentDataSourceAlias,
      targetCanvasId: childCanvasScopeId,
      targetDataSourceAlias: childDataSourceAlias,
      dependsOnSelectionKey: parentSelectionKey,
      targetScopeKey,
      filters: [
        { field: effectiveFkField, operator: "eq", value: parentKeyValue },
      ],
      filterLogic: "AND",
      provenancePath,
    });

    return () => {
      removeRuntimeFilter(filterId);
    };
  }, [
    childCanvasScopeId,
    childDataSourceAlias,
    effectiveFkField,
    element.id,
    parentCanvasScopeId,
    parentDataSourceAlias,
    parentKeyValue,
    parentSelection,
    upsertRuntimeFilter,
    removeRuntimeFilter,
  ]);

  // Fetch child records -- uses refs for authFetch/onChildDataChange to avoid dependency cycles
  const fetchChildRecords = useCallback(
    async (signal?: AbortSignal) => {
      if (!childCanvas || parentKeyValue === undefined) {
        setChildRecords([]);
        return;
      }

      try {
        setRecordsLoading(true);
        setRecordsError(null);

        // Find the data source in child canvas
        // Backend returns data_sources with `alias`; builder uses `name`.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dataSources = ((childCanvas as any).data_sources || []) as any[];
        const dataSource =
          dataSources.find((ds) => {
            const alias = ds.alias ?? ds.name ?? ds.id;
            return String(alias) === String(childDataSourceAlias);
          }) || dataSources[0];

        const viewId = dataSource?.view_id;
        if (!viewId) {
          throw new Error("Child data source not found or has no view");
        }

        // Build filter query via view-rows query endpoint (supports filters properly)
        const filterField = effectiveFkField || "parent_id";
        const response = await authFetchRef.current(
          `/api/workspace/myvault/view-rows/${viewId}/query${childCanvasScopeId ? `?canvas_id=${encodeURIComponent(childCanvasScopeId)}` : ""}`,
          "fetch child records",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-workspace-id": workspaceId,
            },
            body: JSON.stringify({
              filters: [
                { field: filterField, operator: "eq", value: parentKeyValue },
              ],
              page: 1,
              page_size: 1000,
              include_total: false,
            }),
            signal,
          },
        );

        // Don't update state if aborted
        if (
          signal?.aborted ||
          currentRecordsContext.current.scope !== recordsScope
        )
          return;

        const raw =
          response?.data || response?.rows || response?.items || response || [];
        const records: ChildRecord[] = (Array.isArray(raw) ? raw : []).map(
          (r: any) => {
            const rowId = r?._rowId ?? r?.__row_id ?? r?.id;
            if (rowId === undefined || rowId === null || rowId === "") {
              throw new Error(
                "Missing child record identifier; editing is unavailable.",
              );
            }
            return { ...r, __row_id: rowId };
          },
        );

        setChildRecords(records);
        setLoadedRecordsScope(recordsScope);

        // Initialize form states for each record
        const newFormStates: Record<string, Record<string, FieldState>> = {};
        for (const record of records) {
          const recordId = String(record.__row_id);
          newFormStates[recordId] = createFieldStates(record);
        }
        setFormStates(newFormStates);

        onChildDataChangeRef.current?.(config.childCanvasId!, records);
      } catch (err) {
        // Ignore abort errors -- they're expected during cleanup
        if (err instanceof DOMException && err.name === "AbortError") return;
        if (signal?.aborted) return;
        console.error("Failed to fetch child records:", err);
        setRecordsError(
          err instanceof Error ? err.message : "Failed to fetch records",
        );
      } finally {
        if (!signal?.aborted) {
          setRecordsLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      childCanvas,
      config,
      parentKeyValue,
      effectiveFkField,
      workspaceId,
      allowCreate,
      allowEdit,
      allowDelete,
      recordsScope,
    ],
  );

  // Fetch records when dependencies change, with proper abort cleanup.
  // refreshCounter drives re-fetches after CRUD operations so all fetches go through
  // this useEffect with a proper AbortController lifecycle (no race conditions).
  useEffect(() => {
    if (!childCanvas || parentKeyValue === undefined || !capabilitiesReady)
      return;

    const abortController = new AbortController();
    fetchChildRecords(abortController.signal);

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    childCanvas,
    parentKeyValue,
    effectiveFkField,
    workspaceId,
    refreshCounter,
    capabilitiesReady,
    recordsScope,
  ]);

  // Create field states for a record
  function createFieldStates(record: ChildRecord): Record<string, FieldState> {
    const states: Record<string, FieldState> = {};

    for (const el of renderableElements) {
      if (el.type === "field" && el.data_binding?.field_path) {
        const fieldPath = el.data_binding.field_path;
        const value = record[fieldPath];

        states[fieldPath] = {
          value,
          touched: false,
          error: undefined,
          onChange: writableChildFields.includes(el)
            ? (newValue: unknown) => {
                setFormStates((prev) => ({
                  ...prev,
                  [String(record.__row_id)]: {
                    ...prev[String(record.__row_id)],
                    [fieldPath]: {
                      ...prev[String(record.__row_id)]?.[fieldPath],
                      value: newValue,
                      touched: true,
                    },
                  },
                }));
              }
            : undefined,
          onBlur: writableChildFields.includes(el)
            ? () => {
                setFormStates((prev) => ({
                  ...prev,
                  [String(record.__row_id)]: {
                    ...prev[String(record.__row_id)],
                    [fieldPath]: {
                      ...prev[String(record.__row_id)]?.[fieldPath],
                      touched: true,
                    },
                  },
                }));
              }
            : undefined,
        };
      }
    }

    return states;
  }

  // Handle save record
  const handleSaveRecord = async (recordId: string | number) => {
    if (
      !childCanvas ||
      !childCanvasScopeId ||
      parentRowId == null ||
      !canEditChild ||
      !mayWriteCurrentRecords()
    )
      return;

    const recordFormState = formStates[String(recordId)];
    if (!recordFormState) return;

    // Build update data from form state
    const updateData: Record<string, unknown> = {};
    for (const field of writableChildFields) {
      const fieldPath = field.data_binding?.field_path;
      if (fieldPath && recordFormState[fieldPath])
        updateData[fieldPath] = recordFormState[fieldPath].value;
    }

    try {
      await authFetch(
        withEmbeddingPath(
          `/api/workspace/myvault/canvases/${childCanvasScopeId}/submit?parent_row_id=${encodeURIComponent(String(parentRowId))}`,
          embeddingPath,
        ),
        "update child record",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-workspace-id": workspaceId,
          },
          body: JSON.stringify({
            data_source_alias: childDataSourceAlias,
            record_id: recordId,
            data: updateData,
          }),
        },
      );

      setEditingRecordId(null);
      setRefreshCounter((c) => c + 1);
    } catch (err) {
      console.error("Failed to save record:", err);
    }
  };

  // Handle create new record
  const handleCreateRecord = async () => {
    if (
      !childCanvas ||
      !childCanvasScopeId ||
      parentRowId == null ||
      !canCreateChild ||
      !mayWriteCurrentRecords() ||
      parentKeyValue === undefined
    )
      return;

    try {
      const createData: Record<string, unknown> = Object.fromEntries(
        writableChildFields
          .map((field) => field.data_binding?.field_path)
          .filter((path): path is string => !!path)
          .map((path) => [path, newRecordData[path]]),
      );

      await authFetch(
        withEmbeddingPath(
          `/api/workspace/myvault/canvases/${childCanvasScopeId}/submit?parent_row_id=${encodeURIComponent(String(parentRowId))}`,
          embeddingPath,
        ),
        "create child record",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-workspace-id": workspaceId,
          },
          body: JSON.stringify({
            data_source_alias: childDataSourceAlias,
            data: createData,
          }),
        },
      );

      setIsCreating(false);
      setNewRecordData({});
      setRefreshCounter((c) => c + 1);
    } catch (err) {
      console.error("Failed to create record:", err);
    }
  };

  // Handle delete record
  const handleDeleteRecord = async (recordId: string | number) => {
    if (
      !childCanvas ||
      !childCanvasScopeId ||
      !allowDelete ||
      !mayWriteCurrentRecords()
    )
      return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataSources = ((childCanvas as any).data_sources || []) as any[];
      const dataSource =
        dataSources.find((ds) => {
          const alias = ds.alias ?? ds.name ?? ds.id;
          return String(alias) === String(childDataSourceAlias);
        }) || dataSources[0];

      const viewId = dataSource?.view_id;
      if (!viewId) {
        throw new Error("Child data source not found");
      }

      await authFetch(
        withEmbeddingPath(
          `/api/workspace/myvault/view-rows/${viewId}/rows/${recordId}?canvas_id=${encodeURIComponent(childCanvasScopeId)}`,
          embeddingPath,
        ),
        "delete child record",
        {
          method: "DELETE",
          headers: { "x-workspace-id": workspaceId },
        },
      );

      setRefreshCounter((c) => c + 1);
    } catch (err) {
      console.error("Failed to delete record:", err);
    }
  };

  // Toggle record expansion
  const toggleExpanded = (recordId: string | number) => {
    setExpandedRecords((prev) => {
      const next = new Set(prev);
      if (next.has(recordId)) {
        next.delete(recordId);
      } else {
        next.add(recordId);
      }
      return next;
    });
  };

  // Not configured state
  if (!config.childCanvasId) {
    return (
      <Alert color="warning">
        Embedded canvas not configured. Please configure the child canvas in the
        builder.
      </Alert>
    );
  }

  // Loading state
  if (
    canvasLoading ||
    (!recordsScopeMatches &&
      !recordsError &&
      parentKeyValue !== undefined &&
      parentRowId != null) ||
    (recordsLoading && childRecords.length === 0) ||
    capabilitiesLoading
  ) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  // Error state
  if (canvasError || recordsError) {
    return <Alert color="danger">{canvasError || recordsError}</Alert>;
  }

  // No parent key value - can't filter records
  if (parentKeyValue === undefined || parentRowId == null) {
    return (
      <Alert color="neutral">
        Parent record must be saved before managing related records.
      </Alert>
    );
  }

  if (!childCanvas) {
    return <Alert color="warning">Child canvas not found.</Alert>;
  }

  const refreshOverlay = recordsLoading ? (
    <Box
      role="status"
      aria-label="Refreshing records"
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 2,
        bgcolor: "background.surface",
        opacity: 0.65,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress size="sm" />
    </Box>
  ) : null;

  // Collections can render without a child row only when the child contains no
  // record-bound content. Mixed children use the existing record form path so
  // FILE and RELATED_RECORDS receive the actual filtered child identity/values.
  const hasRecordContent = renderableElements.some(
    (childElement) =>
      childElement.type === "field" || childElement.type === "related_records",
  );
  if (hasCollectionWidget && !hasRecordContent) {
    return (
      <Card variant="outlined" sx={{ ...surfaceSx, position: "relative" }}>
        {refreshOverlay}
        <CardContent>
          {showTitle && (
            <Typography level="title-md" sx={{ mb: compact ? 1 : 2 }}>
              {childCanvas.name}
            </Typography>
          )}
          <CanvasRuntimeProvider
            value={{
              workspaceId,
              canvasId: childCanvasScopeId,
              dataSources: childDataSources,
              canEdit: allowEdit,
              onRefresh: () => setRefreshCounter((value) => value + 1),
              embeddingPath,
              rowId: null,
              rowData: effectiveParentData || null,
              interactionBus: runtime?.interactionBus,
            }}
          >
            <CanvasRenderer
              elements={renderableElements}
              config={RUNTIME_GRID_CONFIG}
              mode="runtime"
              responsive
              fieldState={{}}
              renderViewGrid={renderNestedViewGrid}
              renderTimeline={renderNestedTimeline}
              renderDataWidget={renderNestedDataWidget}
              renderEmbeddedCanvas={(childElement) =>
                renderNestedEmbeddedCanvas(childElement, effectiveParentData)
              }
            />
          </CanvasRuntimeProvider>
        </CardContent>
      </Card>
    );
  }

  // Render single record form (for 0 or 1 records)
  if (childRecords.length <= 1 && !isCreating) {
    const record = childRecords[0];
    const recordFormState = record
      ? formStates[String(record.__row_id)] || {}
      : {};

    // Build form state for new record (when no record exists)
    const newRecordFormState: Record<string, FieldState> = {};
    if (!record) {
      for (const el of renderableElements) {
        const fieldPath = el.data_binding?.field_path;
        if (fieldPath) {
          newRecordFormState[fieldPath] = {
            value: newRecordData[fieldPath] ?? "",
            onChange: writableChildFields.includes(el)
              ? (val: unknown) =>
                  setNewRecordData((prev) => ({ ...prev, [fieldPath]: val }))
              : undefined,
          };
        }
      }
    }

    return (
      <Card variant="outlined" sx={{ ...surfaceSx, position: "relative" }}>
        {refreshOverlay}
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: compact ? 1 : 2 }}
          >
            {showTitle && (
              <Typography level="title-md">{childCanvas.name}</Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
              {record && canEditChild && (
                <Button
                  size="sm"
                  variant="soft"
                  color="primary"
                  startDecorator={<SaveIcon />}
                  onClick={() => handleSaveRecord(record.__row_id)}
                >
                  Save
                </Button>
              )}
              {!record && canCreateChild && (
                <Button
                  size="sm"
                  variant="soft"
                  color="success"
                  startDecorator={<SaveIcon />}
                  onClick={handleCreateRecord}
                  disabled={Object.keys(newRecordData).length === 0}
                >
                  Save New
                </Button>
              )}
              {record && allowDelete && (
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  aria-label="Delete child record"
                  onClick={() => handleDeleteRecord(record.__row_id)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          </Stack>

          <CanvasRuntimeProvider
            value={{
              workspaceId,
              canvasId: childCanvasScopeId,
              dataSources: childDataSources,
              canEdit: allowEdit,
              onRefresh: () => setRefreshCounter((value) => value + 1),
              embeddingPath,
              rowId: record ? String(record.__row_id) : null,
              rowData: record || newRecordData,
              interactionBus: runtime?.interactionBus,
            }}
          >
            <CanvasRenderer
              elements={renderableElements}
              config={RUNTIME_GRID_CONFIG}
              mode="runtime"
              responsive
              fieldState={record ? recordFormState : newRecordFormState}
              renderViewGrid={renderNestedViewGrid}
              renderTimeline={renderNestedTimeline}
              renderDataWidget={renderNestedDataWidget}
              renderEmbeddedCanvas={(childElement) =>
                renderNestedEmbeddedCanvas(
                  childElement,
                  record || newRecordData,
                )
              }
            />
          </CanvasRuntimeProvider>
        </CardContent>
      </Card>
    );
  }

  // Render list view (for multiple records)
  return (
    <Card variant="outlined" sx={{ ...surfaceSx, position: "relative" }}>
      {refreshOverlay}
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: compact ? 1 : 2 }}
        >
          <Typography level="title-md" component="div">
            {showTitle ? childCanvas.name : null}
            <Chip size="sm" sx={{ ml: 1 }}>
              {childRecords.length}
            </Chip>
          </Typography>
          {canCreateChild && (
            <Button
              size="sm"
              variant="soft"
              color="success"
              startDecorator={<AddIcon />}
              onClick={() => setIsCreating(true)}
              disabled={isCreating}
            >
              Add
            </Button>
          )}
        </Stack>

        <Stack spacing={1}>
          {/* New record form */}
          {isCreating && canCreateChild && (
            <Card variant="soft" color="success" sx={{ p: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography level="title-sm">New Record</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="sm"
                    variant="soft"
                    color="success"
                    startDecorator={<SaveIcon />}
                    onClick={handleCreateRecord}
                  >
                    Save
                  </Button>
                  <IconButton
                    size="sm"
                    variant="soft"
                    color="neutral"
                    onClick={() => {
                      setIsCreating(false);
                      setNewRecordData({});
                    }}
                  >
                    <CancelIcon />
                  </IconButton>
                </Stack>
              </Stack>
              <CanvasRuntimeProvider
                value={{
                  workspaceId,
                  canvasId: childCanvasScopeId,
                  dataSources: childDataSources,
                  canEdit: allowEdit,
                  onRefresh: () => setRefreshCounter((value) => value + 1),
                  embeddingPath,
                  rowId: null,
                  rowData: newRecordData,
                  interactionBus: runtime?.interactionBus,
                }}
              >
                <CanvasRenderer
                  elements={renderableElements}
                  config={RUNTIME_GRID_CONFIG}
                  mode="runtime"
                  responsive
                  fieldState={createNewRecordFieldStates()}
                  renderViewGrid={renderNestedViewGrid}
                  renderTimeline={renderNestedTimeline}
                  renderDataWidget={renderNestedDataWidget}
                  renderEmbeddedCanvas={(childElement) =>
                    renderNestedEmbeddedCanvas(childElement, newRecordData)
                  }
                />
              </CanvasRuntimeProvider>
            </Card>
          )}

          {/* Existing records */}
          {childRecords.map((record) => {
            const recordId = record.__row_id;
            const isExpanded = expandedRecords.has(recordId);
            const isEditing = editingRecordId === recordId;
            const recordFormState = formStates[String(recordId)] || {};

            // Get display value for collapsed view
            const displayValue =
              Object.values(record).find(
                (v) =>
                  typeof v === "string" &&
                  v.length > 0 &&
                  v !== String(recordId),
              ) || `Record ${recordId}`;

            return (
              <Card key={recordId} variant="outlined">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "background.level1" },
                  }}
                  onClick={() => toggleExpanded(recordId)}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton size="sm" variant="plain">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography level="body-sm">
                      {String(displayValue)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    {canEditChild && !isEditing && (
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingRecordId(recordId);
                          setExpandedRecords((prev) =>
                            new Set(prev).add(recordId),
                          );
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {isEditing && canEditChild && (
                      <>
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="success"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveRecord(recordId);
                          }}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="neutral"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingRecordId(null);
                          }}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    {allowDelete && (
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRecord(recordId);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
                {isExpanded && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <CanvasRuntimeProvider
                        value={{
                          workspaceId,
                          canvasId: childCanvasScopeId,
                          dataSources: childDataSources,
                          canEdit: allowEdit,
                          onRefresh: () =>
                            setRefreshCounter((value) => value + 1),
                          embeddingPath,
                          rowId: String(recordId),
                          rowData: record,
                          interactionBus: runtime?.interactionBus,
                        }}
                      >
                        <CanvasRenderer
                          elements={renderableElements}
                          config={RUNTIME_GRID_CONFIG}
                          mode={isEditing ? "runtime" : "edit"}
                          responsive
                          fieldState={recordFormState}
                          renderViewGrid={renderNestedViewGrid}
                          renderTimeline={renderNestedTimeline}
                          renderDataWidget={renderNestedDataWidget}
                          renderEmbeddedCanvas={(childElement) =>
                            renderNestedEmbeddedCanvas(childElement, record)
                          }
                        />
                      </CanvasRuntimeProvider>
                    </Box>
                  </>
                )}
              </Card>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );

  // Helper to create field states for new record
  function createNewRecordFieldStates(): Record<string, FieldState> {
    const states: Record<string, FieldState> = {};

    for (const el of renderableElements) {
      if (el.type === "field" && el.data_binding?.field_path) {
        const fieldPath = el.data_binding.field_path;

        states[fieldPath] = {
          value: newRecordData[fieldPath],
          touched: false,
          error: undefined,
          onChange: writableChildFields.includes(el)
            ? (newValue: unknown) => {
                setNewRecordData((prev) => ({
                  ...prev,
                  [fieldPath]: newValue,
                }));
              }
            : undefined,
          onBlur: () => {},
        };
      }
    }

    return states;
  }
}

export default EmbeddedCanvasRenderer;
